import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'

import { currentLanguage } from './language.js'

/**
 * Where the API lives. Same-origin in production behind one hostname pair,
 * localhost:4000 in development. `VITE_API_URL` is baked in at build time, so
 * the deployed bundle carries the staging or production value.
 */
export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000'

/**
 * `credentials: 'include'` is the whole reason the API's CORS allowlist names
 * one origin and sets `credentials: true`. The session is an httpOnly cookie
 * (V1.md section 6): there is no token for this client to hold, and nothing to
 * put in an Authorization header.
 */
export function createApolloClient(): ApolloClient {
  return new ApolloClient({
    /**
     * Two links: state the page's language, then send the request.
     *
     * `SetContextLink` rather than `HttpLink`'s own `headers`, because that
     * option is a fixed object read once when the client is built and the
     * answer here changes with the URL. A context setter runs per operation,
     * so a language switch is carried by the next query rather than needing a
     * new client.
     *
     * The API needs this for the one thing it renders words for: the subject
     * and education-level catalogs (V1.md section 18). Without it a tutor on
     * /en/practices would read English chrome above Spanish subject names,
     * because the server would fall back to their stored preference.
     *
     * `accept-language` is CORS-safelisted, so stating it adds no preflight.
     */
    link: new SetContextLink((previous) => ({
      ...previous,
      headers: {
        ...(previous.headers as Record<string, string> | undefined),
        'accept-language': currentLanguage(),
      },
    })).concat(new HttpLink({ uri: `${API_URL}/graphql`, credentials: 'include' })),
    cache: new InMemoryCache({
      typePolicies: {
        /**
         * The student's progress has no id, so the cache keeps it inline under
         * `myProgress`, and two screens ask for different parts of it: the
         * home one count, the progress screen each practice's run. Merged
         * rather than replaced, so reading one does not throw the other away
         * and send its screen back to the network.
         */
        StudentProgress: { merge: true },
      },
    }),
  })
}

/**
 * The authentication endpoints. None of these is a GraphQL call.
 *
 * Better Auth owns /api/auth/* and brings its own CSRF handling, rate limiting
 * and cookie logic; wrapping any of it in a mutation would mean reimplementing
 * that behind a second door (V1.md section 5). GraphQL exposes `me` and
 * nothing else about the mechanism.
 *
 * Every one of them is called with `credentials: 'include'`, because the
 * session is an httpOnly cookie and there is no token for this client to hold.
 */
export const AUTH_ENDPOINTS = {
  /** Development only: POST { email } — creates the normal session cookie. */
  signInWithEmail: `${API_URL}/api/auth/sign-in/development-email`,
  /** POST { email, type: 'sign-in' } — emails a six-digit code. */
  sendCode: `${API_URL}/api/auth/email-otp/send-verification-otp`,
  /** POST { email, otp } — exchanges the code for a session cookie. */
  signInWithCode: `${API_URL}/api/auth/sign-in/email-otp`,
  /** POST { provider, callbackURL, errorCallbackURL } — answers with a URL to navigate to. */
  signInWithProvider: `${API_URL}/api/auth/sign-in/social`,
  /** POST. Not a link: Better Auth's sign-out endpoint refuses a GET. */
  signOut: `${API_URL}/api/auth/sign-out`,
} as const

/**
 * Which providers this deployment offers. Athena's own route, not Better
 * Auth's — the credentials are server-side, and a `VITE_` twin would be a
 * second place for the same fact to be wrong.
 */
export const AUTH_METHODS_ENDPOINT = `${API_URL}/api/auth-methods`
